import React from 'react';
import { CreditCard, Calendar } from 'lucide-react';
import Select from '../../../components/ui/Select';

const PaymentTermsSection = ({ paymentTerms, onChange }) => {
  const paymentTermsOptions = [
    { value: 'due-on-receipt', label: 'Due on Receipt' },
    { value: 'net-15', label: 'Net 15 Days' },
    { value: 'net-30', label: 'Net 30 Days' },
    { value: 'net-45', label: 'Net 45 Days' },
    { value: 'net-60', label: 'Net 60 Days' },
    { value: 'net-90', label: 'Net 90 Days' },
    { value: '2-10-net-30', label: '2/10 Net 30 (2% discount if paid within 10 days)' },
    { value: 'custom', label: 'Custom Terms' }
  ];

  const getPaymentTermDescription = (term) => {
    switch (term) {
      case 'due-on-receipt':
        return 'Payment is due immediately upon receipt of this invoice.';
      case 'net-15':
        return 'Payment is due within 15 days of invoice date.';
      case 'net-30':
        return 'Payment is due within 30 days of invoice date.';
      case 'net-45':
        return 'Payment is due within 45 days of invoice date.';
      case 'net-60':
        return 'Payment is due within 60 days of invoice date.';
      case 'net-90':
        return 'Payment is due within 90 days of invoice date.';
      case '2-10-net-30':
        return 'Take 2% discount if paid within 10 days, otherwise full payment due in 30 days.';
      case 'custom':
        return 'Custom payment terms as agreed upon.';
      default:
        return 'Standard payment terms apply.';
    }
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Payment Terms</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Terms Selection */}
        <div className="space-y-4">
          <Select
            label="Payment Terms"
            options={paymentTermsOptions}
            value={paymentTerms}
            onChange={onChange}
            required
            className="w-full"
          />

          {paymentTerms && (
            <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800 mb-1">
                    Payment Terms Description
                  </p>
                  <p className="text-sm text-blue-700">
                    {getPaymentTermDescription(paymentTerms)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Methods Info */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-foreground">Accepted Payment Methods</h3>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium">Bank Transfer</p>
                <p className="text-xs text-muted-foreground">Direct bank transfer (ACH/Wire)</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium">Credit/Debit Cards</p>
                <p className="text-xs text-muted-foreground">Visa, MasterCard, American Express</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium">Online Payment</p>
                <p className="text-xs text-muted-foreground">PayPal, Stripe, etc.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Late Payment Policy */}
      <div className="mt-6 pt-6 border-t">
        <h3 className="text-sm font-medium text-foreground mb-3">Late Payment Policy</h3>
        <div className="p-4 bg-yellow-50 rounded-md border border-yellow-200">
          <p className="text-sm text-yellow-800">
            <strong>Important:</strong> Late payments may be subject to a 1.5% monthly service charge. 
            Accounts over 30 days past due may be suspended until payment is received.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentTermsSection;